import { useCallback, useEffect, useMemo, useState } from 'react';

import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';

import { MODAL_WIDTHS } from '@/shared/lib/constants';
import { BaseModal } from '@/shared/ui/BaseModal';

import { createPodMetricLineChartOption, type PodMetricLineChartPodHistory } from '@/widgets/monitoring/shared';

import styles from './PodMetricDetailModal.module.css';

type PodMetricDetailModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  unit: string;
  podHistory: PodMetricLineChartPodHistory[];
};

export const PodMetricDetailModal = ({ open, onClose, title, unit, podHistory }: PodMetricDetailModalProps) => {
  const [selectedSeries, setSelectedSeries] = useState<Record<string, boolean>>({});
  const hasPodHistory = podHistory.length > 0;
  const podNames = useMemo(() => podHistory.map((pod) => pod.pod).join(','), [podHistory]);

  const visiblePodHistory = useMemo(() => {
    return podHistory.filter((pod) => selectedSeries[pod.pod] !== false);
  }, [podHistory, selectedSeries]);

  const chartOption: EChartsOption = useMemo(() => {
    if (!hasPodHistory || visiblePodHistory.length === 0) {
      return {};
    }

    return createPodMetricLineChartOption({
      podHistory: visiblePodHistory,
      unit,
    });
  }, [hasPodHistory, unit, visiblePodHistory]);

  const handleLegendClick = useCallback((podName: string) => {
    setSelectedSeries((prev) => ({ ...prev, [podName]: !prev[podName] }));
  }, []);

  useEffect(() => {
    if (!podNames) return;

    setSelectedSeries((prev) => {
      const nextSelectedSeries: Record<string, boolean> = {};
      const currentPods = podNames.split(',');

      currentPods.forEach((podName) => {
        nextSelectedSeries[podName] = prev[podName] ?? true;
      });

      return nextSelectedSeries;
    });
  }, [podNames]);

  return (
    <BaseModal open={open} onClose={onClose} footer={null} width={MODAL_WIDTHS.LARGE_PANEL} title={title}>
      <div className={styles.content}>
        <div className={styles.chartLayout}>
          <div className={styles.chartPanel}>
            <ReactECharts
              option={chartOption}
              className={styles.chart}
              style={{ height: '484px', width: '440px' }}
              opts={{ renderer: 'canvas' }}
              notMerge={true}
              lazyUpdate={false}
            />
          </div>
          <div className={styles.legendPanel}>
            {hasPodHistory ? (
              podHistory.map((pod) => {
                const isSelected = selectedSeries[pod.pod] !== false;

                return (
                  <button
                    type="button"
                    key={pod.pod}
                    className={`${styles.legendItem} ${!isSelected && styles.legendItemInactive}`}
                    onClick={() => handleLegendClick(pod.pod)}
                    aria-pressed={isSelected}
                    aria-label={`${pod.pod} 범례 토글`}>
                    <span className={styles.legendMarkerWrapper}>
                      <span className={styles.legendMarker} style={{ backgroundColor: pod.color }} />
                    </span>
                    <span className={styles.legendText} title={pod.pod}>
                      {pod.pod}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className={styles.chartEmpty}>
                <span className={styles.chartEmptyLabel}>데이터가 존재하지 않습니다.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </BaseModal>
  );
};
